import { forwardRef } from 'react';
import { IconButton, Stack } from '@figma/fpl-components';
import { Icon24Minus, Icon24Plus } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { ToolInputRow } from './ToolInputControls';
import type { ComponentProps, ComponentPropsWithoutRef, ReactNode } from 'react';

type ListAction = {
  label: string;
  onClick: () => void;
  disabled: boolean;
};

type ToolInputListProps = Pick<
  ComponentProps<typeof ToolInputRow>,
  'label' | 'required' | 'description' | 'disabled'
> & {
  add: ListAction;
  count?: ReactNode;
  scrollable?: boolean;
  footer?: ReactNode;
  children: ReactNode;
};

export function ToolInputList({
  label,
  required,
  description,
  disabled,
  add,
  count,
  scrollable = false,
  footer,
  children,
}: ToolInputListProps) {
  return (
    <ToolInputRow
      label={label}
      required={required}
      description={description}
      disabled={disabled}
      headerActions={
        <Stack.Row gap={cssVars.spacer.x2}>
          {count}
          <IconButton aria-label={add.label} disabled={disabled || add.disabled} onClick={add.onClick}>
            <Icon24Plus />
          </IconButton>
        </Stack.Row>
      }
    >
      <Stack.Col gap={cssVars.spacer.x2}>
        <Stack.Col gap={cssVars.spacer.x2} {...stylex.props(scrollable ? styles.scrollableList : styles.list)}>
          {children}
        </Stack.Col>
        {footer}
      </Stack.Col>
    </ToolInputRow>
  );
}

type ToolInputListRowProps = ComponentPropsWithoutRef<typeof Stack.Row> & {
  remove: ListAction;
};

export const ToolInputListRow = forwardRef<HTMLDivElement, ToolInputListRowProps>(function ToolInputListRow(
  { remove, children, gap = cssVars.spacer.x1, ...props },
  ref,
) {
  return (
    <Stack.Row ref={ref} gap={gap} {...props}>
      {children}
      <IconButton aria-label={remove.label} disabled={remove.disabled} onClick={remove.onClick}>
        <Icon24Minus />
      </IconButton>
    </Stack.Row>
  );
});

const styles = stylex.create({
  list: {
    paddingBlock: spacer.spacer4px,
  },
  scrollableList: {
    maxHeight: 240,
    overflow: 'auto',
    paddingBlock: 2,
    '--icon-button-focus-outline-offset': '-1px',
  },
});
